import { CSSProperties, FC, useEffect, useState } from "react";
import {
    Pane,
    PaneProps,
    SelectMenu,
    SelectMenuItem,
    TextInputField,
} from "evergreen-ui";
import { useStoreListQuery } from "../datastore/StoreDataStore";
import { Store } from "../../../common/models";
import { generatePath } from "react-router-dom";
import { NEW_STORE_ROUTE_PATH } from "../navigation/Routes";
import { StoreTabIds } from "../pages/Store/MultiTabNewStorePage";

// TODO: Consolidate store dialog with StoresList
interface Props extends PaneProps {
    /**
     * Callback that notifies the parent with store data when the selector value changes.
     */
    onStoreChanged: (newStoreName: string, existingStoreId?: string) => void;
    /**
     * Callback that notifies the parent when the selector value has been finalized and will no
     * longer be changing.
     */
    onStoreConfirmed?: () => void;

    initialStore?: Store;
    inputStyle?: CSSProperties;
    required?: boolean;
}

const CREATE_STORE_TEXT = "+ New Store";
const CREATE_STORE_VALUE = "new_store_value";

// This is the default height of a SelectMenu item that is set by Evergreen.
const ROW_HEIGHT_PX = 33;

// The Evergreen `SelectMenu` component doesn't allow us to set a class or id on the
// element, nor does it have a `maxHeight`, so we have to calculate the `height` ourselves.
const MAX_ROW_COUNT = 10;

export const StoreSelector: FC<Props> = (props: Props) => {
    const {
        onStoreChanged,
        onStoreConfirmed,
        initialStore = null,
        inputStyle,
        required = false,
        ...paneProps
    } = props;
    const { data, loading } = useStoreListQuery();
    const [stores, setStores] = useState<Store[]>([]);
    const [selectedStore, setSelectedStore] = useState<Store | null>(initialStore);

    useEffect(() => {
        if (!loading) {
            setStores(data || []);
        }
    }, [data, loading]);

    const renderClickableText = () => {
        return (
            <TextInputField
                style={{
                    cursor: "pointer",
                    ...inputStyle
                }}
                placeholder={loading ? "Loading..." : "Click to select a Store"}
                label={"Store Name"}
                readOnly
                required={required}
                value={selectedStore?.name || ""}
            />
        );
    };

    const calculateSelectMenuHeightPx = (): number => {
        // We add an extra "+ New Store" row to the list of stores.
        const rowCount = stores.length + 1;

        // Cap the scrollable height, otherwise we run into issues with the popover element,
        // particularly for internal users who are connected to many stores.
        return ROW_HEIGHT_PX * Math.min(MAX_ROW_COUNT, rowCount);
    };

    const buildOptionsList = (): SelectMenuItem[] => {
        const optionsList = stores.map((store: Store) => ({ label: store.name, value: store.id }));
        const createNewStoreItem = { label: CREATE_STORE_TEXT, value: CREATE_STORE_VALUE };

        // Insert the "+ New Store" item at the front of the list if the view is scrollable, since
        // users may not know to scroll all the way to the bottom to find this button.
        return (stores.length >= MAX_ROW_COUNT) ?
            [createNewStoreItem, ...optionsList] :
            [...optionsList, createNewStoreItem];
    };

    return (
        <Pane {...paneProps}>
            {loading ? renderClickableText() : (
                <SelectMenu
                    closeOnSelect
                    hasFilter={false}
                    hasTitle={false}
                    height={calculateSelectMenuHeightPx()}
                    options={buildOptionsList()}
                    onSelect={(item) => {
                        if (item.value === CREATE_STORE_VALUE) {
                            setSelectedStore(null);
                            // Redirect the user to the new store creation page in a new tab to ensure the current flow still works.
                            window.open(generatePath(NEW_STORE_ROUTE_PATH, {
                                tabId: StoreTabIds.details,
                            }), "_blank");
                        } else {
                            setSelectedStore(stores.find((store: Store) => store.id === item.value) ?? null);
                            onStoreChanged(item.label, String(item.value));
                        }
                    }}
                >
                    {renderClickableText()}
                </SelectMenu>
            )}
        </Pane>
    );
};
